import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Trash2, Loader } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import DictationService, {
  DictationItem,
} from '../../services/dictationService';

interface DictationHistoryProps {
  onCopy?: (text: string) => void;
}

const DictationHistory: React.FC<DictationHistoryProps> = ({ onCopy }) => {
  const { t } = useTranslation();
  const [dictations, setDictations] = useState<DictationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDictations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await DictationService.getDictations();
      setDictations(data);
    } catch (err) {
      console.error('Failed to load dictations:', err);
      setError(
        t(
          'dictation.history.errors.failedToLoad',
          'Failed to load dictation history',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  // Load dictations on mount
  useEffect(() => {
    loadDictations();
  }, []);

  const handleCopy = async (text: string) => {
    try {
      await DictationService.copyToClipboard(text);
      if (onCopy) {
        onCopy(text);
      }
    } catch (err) {
      console.error('Failed to copy dictation:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await DictationService.deleteDictation(id);
      setDictations((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error('Failed to delete dictation:', err);
      setError(
        t(
          'dictation.history.errors.failedToDelete',
          'Failed to delete dictation',
        ),
      );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (dictations.length === 0) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-semibold text-muted-foreground mb-2">
          {t('dictation.history.empty.title', 'No dictations yet')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t(
            'dictation.history.empty.description',
            'Start recording to see your dictation history here',
          )}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="max-h-[300px] overflow-auto">
        {dictations.map((dictation, index) => (
          <React.Fragment key={dictation.id}>
            <div className="group p-4 hover:bg-accent/50 transition-colors">
              <div className="flex justify-between items-start gap-4 mb-2">
                <p className="flex-1 text-sm leading-relaxed line-clamp-3">
                  {dictation.text}
                </p>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => handleCopy(dictation.text)}
                    title={t(
                      'dictation.history.actions.copyToClipboard',
                      'Copy to clipboard',
                    )}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 hover:text-destructive"
                    onClick={() => handleDelete(dictation.id)}
                    title={t(
                      'dictation.history.actions.deleteDictation',
                      'Delete dictation',
                    )}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <span className="text-xs text-muted-foreground">
                {formatDate(dictation.createdAt)}
              </span>
            </div>
            {index < dictations.length - 1 && <Separator />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default DictationHistory;
